import {useEffect, useState, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {Box} from '@mui/material';
import {useSnackbar} from 'notistack';
import TabsComponent from '../../sub-modules-menu/tabsComponent/TabsComponent';
import {UIData} from './UserAccessUtils';
import {
  BASE_ROUTE,
  BASE_ROUTE_MODULE,
  BASE_MENU_ITEM
} from 'src/routes/BaseRoute';
import UserGroupsAssigningDialog from '@components/dialog/user-groups-assigning-dialog/UserGroupsAssigningDialog';
import UserGroupsDeleteDialog from '@components/dialog/user-groups-delete-dialog/UserGroupsDeleteDialog';
import CustomBreadcrumbs from 'src/minimal-components/custom-breadcrumbs/custom-breadcrumbs';
import NewUserForm from './NewUserForm';
import EncryptUtils from 'src/utils/EncryptUtils';
import {useAppContext} from '@context/AppContext';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {
  getSearchResultsData,
  addNewUserData
} from '@shared/services/service-handlers/PrivateService';
import FormConstants from '@shared/constants/FormConstants';
import {emailValidator} from '@shared/utils/Validate';
import {getErrorMessage} from '@shared/utils/ErrorUtils';

interface UserAccessProps {
  module?: string;
}
interface StateProps {
  addNew: boolean;
  addNewConfig: any;
  loader: boolean;
}

const UserAccess = (props: UserAccessProps): React.ReactElement => {
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const {module} = props;
  const {
    appState: {applicationName}
  } = useAppContext();

  const [state, setState] = useState<StateProps>({
    addNew: false,
    addNewConfig: [],
    loader: false
  });
  const [openEditPopup, setOpenEditPopup] = useState<boolean>(false);
  const [openDeletePopup, setOpenDeletePopup] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<any>({});
  const [selectedDeleteUser, setSelectedDeleteUser] = useState<any>({});
  const [apiLoader, setApiLoader] = useState<boolean>(false);
  const {addNew, addNewConfig, loader} = state;

  const init = () => {
    setState((prev: any) => ({
      ...prev,
      addNewConfig: UIData?.sub_sections[0]?.add_new?.form
    }));
  };

  useEffect(() => {
    init();
  }, []);

  const handleAddNewRecord = () => {
    setState((prev: any) => ({...prev, addNew: true}));
    navigate(
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${module}?${BASE_MENU_ITEM}=${FormConstants.MENU_ITEM.USER_ACCESS}`,
      {state: {}}
    );
  };

  const handleCancelButton = () => {
    setState((prev: any) => ({...prev, addNew: false}));
    navigate(`/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}`, {
      state: {}
    });
  };

  const handleButtonSubmit = async (data: any) => {
    const payload: any = {...data};

    // Validate email
    if (!emailValidator?.(payload.email_id)) {
      setState(prev => ({
        ...prev,
        formError: 'Invalid email address'
      }));
      return;
    }

    try {
      const orgField =
        addNewConfig?.fields?.find?.((f: any) => f.name === 'organization') ||
        addNewConfig?.form?.fields?.find?.(
          (f: any) => f.name === 'organization'
        );

      const orgOptions: any[] = orgField?.options ?? [];
      const match = orgOptions.find(
        (o: any) => o.value === payload.organization
      );
      if (match && match.label) {
        payload.organization_id = match.label;
      }
    } catch (err) {
      console.log(err, 'err');
    }

    try {
      const tk = EncryptUtils.encryptedData(payload);

      setState(prev => ({...prev, loader: true}));

      const res = await addNewUserData({tk}, applicationName);

      if (res) {
        enqueueSnackbar(
          res?.message ?? 'User created',
          SnackBarConfig.getSuccess()
        );
        setState(prev => ({...prev, loader: false, addNew: false}));
        handleCancelButton();
      }
    } catch (error: any) {
      setState(prev => ({...prev, loader: false}));
      const errMessage = getErrorMessage?.(error) ?? 'Failed to add user.';
      const email = error?.data?.email;
      enqueueSnackbar(
        `${errMessage} ${email ?? ''}`,
        SnackBarConfig.getError()
      );
    }
  };

  const loadOptions = useCallback(
    async (_fieldName: string, field: any) => {
      if (!field || field.type !== 'select' || !field.sql_data?.sql) return [];

      try {
        const tk = EncryptUtils.encryptedData({...field});
        const moduleName =
          state.addNewConfig?.module_name || module || 'user-access';
        const resp = await getSearchResultsData(applicationName, moduleName, {
          tk
        });
        const rows: any[] = resp?.data ?? [];

        const options = rows.reduce<{id: string | number; name: string}[]>(
          (acc, r) => {
            if (r?.id !== undefined && r?.name !== undefined) {
              acc.push({id: r.id, name: r.name});
            }
            return acc;
          },
          []
        );

        return options;
      } catch (e) {
        console.error('loadOptions error for', _fieldName, e);
        return [];
      }
    },
    [applicationName, module, state.addNewConfig?.module_name]
  );

  const handleEditUser = (data: any): void => {
    setOpenEditPopup(true);
    setSelectedUser(data);
    setApiLoader(true);
  };

  const handleDeleteUser = (data: any) => {
    setOpenDeletePopup(true);
    setSelectedDeleteUser(data);
    setApiLoader(true);
  };

  const onClose = (): void => {
    setOpenEditPopup(false);
    setOpenDeletePopup(false);
    setApiLoader(false);
  };

  const renderAddUser = () => (
    <>
      <CustomBreadcrumbs
        heading={''}
        links={[
          {
            name: 'User Access',
            href: `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}`,
            onClick: () => setState(prev => ({...prev, addNew: false}))
          },
          {name: 'Add New User'}
        ]}
        sx={{mt: 2}}
      />
      <Box mt={10}>
        <NewUserForm
          config={addNewConfig}
          onSubmit={handleButtonSubmit}
          onCancel={handleCancelButton}
          loading={loader}
          loadOptions={loadOptions}
        />
      </Box>
    </>
  );

  const renderTabsComponent = () => {
    return (
      <>
        <TabsComponent
          sectionUIData={UIData}
          module={module}
          sectionRowsData={[]}
          handleAddNewRecord={handleAddNewRecord}
          handleEditUser={handleEditUser}
          handleDeleteUser={handleDeleteUser}
          apiLoader={apiLoader}
        />
        {openEditPopup && (
          <UserGroupsAssigningDialog
            open={openEditPopup}
            onClose={onClose}
            selectedUser={selectedUser}
          />
        )}
        {openDeletePopup && (
          <UserGroupsDeleteDialog
            open={openDeletePopup}
            onClose={onClose}
            selectedUser={selectedDeleteUser}
          />
        )}
      </>
    );
  };

  return addNew ? renderAddUser() : renderTabsComponent();
};

export default UserAccess;
